import React, { useEffect, useState } from "react";
import { Bell, User, Headphones } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import useAuthStore from "../../store/useAuthStore";
import useProfileStore from "../../store/customer/useProfileStore";
import useNotificationStore from "../../store/useNotificationStore";

const Navbar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const logoutCustomer = useAuthStore((state) => state.logoutCustomer);
  const userType = useAuthStore((state) => state.userType);
  const { profile, fetchProfile } = useProfileStore();

  // Use the same notification store as builder, but for customer
  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    // Ensure fresh customer profile for greeting text
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlenav = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (userType === "customer") {
      logoutCustomer();
      navigate("/");
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      navigate("/");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Tooltip state for name
  const [showTooltip, setShowTooltip] = useState(false);

  // Active Logic for Navbar Icons
  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);

  const getButtonClass = (path) => {
    return isActive(path)
      ? "w-8 h-8 sm:w-7 sm:h-7 bg-black rounded-full flex justify-center items-center hover:bg-gray-800 transition-colors"
      : "w-8 h-8 sm:w-7 sm:h-7 bg-blue-500 rounded-full flex justify-center items-center hover:bg-blue-600 transition-colors";
  };

  return (
    <div
      className="flex bg-white justify-between items-center px-2 sm:px-4 py-2"
      style={{ borderRadius: "8px", minHeight: "48px" }}
    >
      {/* Responsive greeting/name section */}
      <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
        <div className="relative">
          <div
            className="text-base font-semibold text-gray-800 truncate max-w-[120px] xs:max-w-[180px] sm:max-w-[220px] md:max-w-[300px] lg:max-w-[400px] cursor-pointer select-none"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            tabIndex={0}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            aria-label={profile && profile.name ? `Hello, ${profile.name}` : ""}
          >
            {profile && profile.name ? (
              <>
                <span className="whitespace-nowrap">Hello,</span>{" "}
                <span className="break-words">{profile.name}</span>
              </>
            ) : (
              ""
            )}
          </div>
          {/* Tooltip for full name */}
          {profile && profile.name && showTooltip && (
            <div
              className="absolute left-0 z-50 mt-1 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-pre-line"
              style={{
                minWidth: "max-content",
                maxWidth: "320px",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                top: "100%",
              }}
            >
              {profile.name}
            </div>
          )}
        </div>
      </div>

      {/* Responsive icon and logout section */}
      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
        <button
          onClick={() => handlenav("/customer/profile")}
          className={getButtonClass("/customer/profile")}
        >
          <User color="#ffffff" size={16} />
        </button>
        {/* Notification Bell Icon Button */}
        <button
          onClick={() => handlenav("/customer/notify")}
          className={`${getButtonClass("/customer/notify")} relative`}
        >
          <Bell color="#ffffff" size={16} />
          {/* Notification Badge with unread count */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center border-2 border-white px-1 leading-none z-10">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {/* Support Icon Button */}
        <button
          onClick={() => handlenav("/customer/help")}
          className={getButtonClass("/customer/help")}
        >
          <Headphones color="#ffffff" size={16} />
        </button>
        {userType === "customer" && (
          <button
            onClick={handleLogout}
            className="h-8 px-4 sm:w-20 bg-blue-500 rounded-full flex justify-center items-center transform active:scale-95 transition-all duration-150 hover:bg-blue-600 text-white text-center text-sm font-medium whitespace-nowrap"
            style={{ minWidth: "64px" }}
          >
            Logout
          </button>
        )}
      </div>
    </div>
  );
};

export default Navbar;